/**
 * Work-unit dependencies — entity-configured list (§1E / §4.7).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GitBranch, Loader2, Pencil, Trash2, Check, X } from 'lucide-react';
import { SearchableSelect } from '../../../components/shared/SearchableSelect';
import { CreateDependencyModal } from './CreateDependencyModal';
import type { FacetDefinition, SortOption, TableColumnWithRender } from '../../../components/collection/types';
import {
  createDependency,
  createPriorityDependency,
  deleteDependency,
  deletePriorityDependency,
  getDependencies,
  getPriorities,
  getPriorityDependencies,
  getWorkUnits,
  syncPlanningFromLiveItems,
  updateDependency,
} from '../../../lib/planningEngineService';
import { PLANNING_SYNC_DEP_NOTE_PREFIX } from '../../../lib/itemToPlanningAdapter';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { usePlanningEngine } from './PlanningEngineContext';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import type {
  Dependency,
  DependencyType,
  Priority,
  PriorityDependency,
  WorkUnit,
} from '../../../lib/planningEngineModule.types';
import { dependencyPairKey, exportCsv } from './planningEngineViewShared';

type DepLevel = 'initiative' | 'work_unit';

function DependencyDetailPanel({
  dep,
  fromWu,
  toWu,
  satisfied,
  workUnits,
  onUpdated,
  onDeleted,
}: {
  dep: Dependency;
  fromWu?: WorkUnit;
  toWu?: WorkUnit;
  satisfied: boolean | null;
  workUnits: WorkUnit[];
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(dep);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSynced = dep.notes?.startsWith(PLANNING_SYNC_DEP_NOTE_PREFIX);

  useEffect(() => {
    setDraft(dep);
    setEditing(false);
  }, [dep]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateDependency(dep.id, draft);
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this dependency?')) return;
    setSaving(true);
    try {
      await deleteDependency(dep.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-5 h-full overflow-auto">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-[var(--text-ter)]" />
          <h3 className="text-lg font-semibold">Work unit dependency</h3>
        </div>
        <div className="flex items-center gap-1">
          {!editing && !isSynced && (
            <button type="button" onClick={() => setEditing(true)} className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {!isSynced && (
            <button type="button" onClick={() => void remove()} disabled={saving} className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {isSynced && <p className="text-xs text-[var(--text-ter)]">Materialized from item relationship graph on sync.</p>}
      {error && <div className="text-sm text-[var(--coral)]">{error}</div>}
      {editing ? (
        <div className="space-y-3">
          <SearchableSelect
            options={workUnits.map((w) => ({ value: w.id, label: w.title }))}
            value={draft.fromWorkUnitId}
            onChange={(v) => setDraft({ ...draft, fromWorkUnitId: v })}
            placeholder="Dependent work unit"
          />
          <SearchableSelect
            options={workUnits.map((w) => ({ value: w.id, label: w.title }))}
            value={draft.toWorkUnitId}
            onChange={(v) => setDraft({ ...draft, toWorkUnitId: v })}
            placeholder="Predecessor work unit"
          />
          <select className="w-full text-sm border rounded px-2 py-1.5" value={draft.dependencyType} onChange={(e) => setDraft({ ...draft, dependencyType: e.target.value as DependencyType })}>
            <option value="Hard">Hard</option>
            <option value="Soft">Soft</option>
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.blocking} onChange={(e) => setDraft({ ...draft, blocking: e.target.checked })} />
            Blocking
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[var(--slate)] text-white">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Save
            </button>
            <button type="button" onClick={() => { setDraft(dep); setEditing(false); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border">
              <X className="h-3 w-3" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="space-y-3 text-sm">
          <div><dt className="text-[var(--text-ter)]">Dependent</dt><dd className="font-medium">{fromWu?.title ?? dep.fromWorkUnitId}</dd></div>
          <div><dt className="text-[var(--text-ter)]">Predecessor</dt><dd>{toWu?.title ?? dep.toWorkUnitId}</dd></div>
          <div className="flex gap-6">
            <div><dt className="text-[var(--text-ter)]">Type</dt><dd>{dep.dependencyType}</dd></div>
            <div><dt className="text-[var(--text-ter)]">Blocking</dt><dd>{dep.blocking ? 'Yes' : 'No'}</dd></div>
          </div>
          <div><dt className="text-[var(--text-ter)]">Satisfied (last run)</dt><dd>{satisfied === null ? '—' : satisfied ? 'Yes' : 'No'}</dd></div>
        </dl>
      )}
    </div>
  );
}

const DEP_FACETS: FacetDefinition<string>[] = [
  {
    key: 'dependencyType',
    label: 'Type',
    options: [
      { value: 'Hard', label: 'Hard' },
      { value: 'Soft', label: 'Soft' },
    ],
  },
];

export default function PlanningDependenciesView() {
  const { projectId, activeScenario, planOutput, scopedItemIds } = usePlanningEngine();
  const [level, setLevel] = useState<DepLevel>('initiative');
  const [deps, setDeps] = useState<Dependency[]>([]);
  const [priorityDeps, setPriorityDeps] = useState<PriorityDependency[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const planRecordByWuId = useMemo(
    () => new Map((planOutput?.planRecords ?? []).map((r) => [r.workUnit.id, r])),
    [planOutput],
  );
  const priorityRecordById = useMemo(
    () => new Map((planOutput?.priorityPlanRecords ?? []).map((r) => [r.priority.id, r])),
    [planOutput],
  );
  const wuById = useMemo(() => new Map(workUnits.map((w) => [w.id, w])), [workUnits]);
  const priorityById = useMemo(() => new Map(priorities.map((p) => [p.id, p])), [priorities]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [d, pd, w, p] = await Promise.all([
        getDependencies(projectId),
        getPriorityDependencies(projectId),
        getWorkUnits(projectId),
        getPriorities(projectId),
      ]);
      setDeps(d);
      setPriorityDeps(pd);
      setWorkUnits(w);
      setPriorities(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleSync = useCallback(async () => {
    if (!projectId) return;
    setSyncing(true);
    setError(null);
    try {
      await Promise.all([fetchItems(), fetchRelationships()]);
      await syncPlanningFromLiveItems(projectId, activeScenario?.id ?? null, useItemsStore.getState().items, useItemRelationshipsStore.getState().edges);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncing(false);
    }
  }, [projectId, activeScenario?.id, fetchItems, fetchRelationships, load]);

  // Auto-sync from live items on mount so dependencies appear without a manual step.
  const didAutoSync = useRef(false);
  useEffect(() => {
    if (didAutoSync.current) return;
    didAutoSync.current = true;
    void handleSync();
  }, [handleSync]);

  const handleCreateWu = async (dep: {
    fromId: string;
    toId: string;
    dependencyType: 'Hard' | 'Soft';
    blocking: boolean;
  }) => {
    if (!projectId) return;
    await createDependency(projectId, {
      fromWorkUnitId: dep.fromId,
      toWorkUnitId: dep.toId,
      dependencyType: dep.dependencyType,
      blocking: dep.blocking,
    });
    await load();
  };

  const handleCreatePriority = async (dep: {
    fromId: string;
    toId: string;
    dependencyType: 'Hard' | 'Soft';
    blocking: boolean;
  }) => {
    if (!projectId) return;
    await createPriorityDependency(projectId, {
      fromPriorityId: dep.fromId,
      toPriorityId: dep.toId,
      dependencyType: dep.dependencyType,
      blocking: dep.blocking,
    });
    await load();
  };

  // Existing pairs per grain, so the create modal can refuse an exact duplicate
  // of a dependency that is already present — including one materialized from a
  // Prioritization relationship, which carries its own id and would otherwise
  // render as a second, identical row.
  const existingWuPairs = useMemo(
    () => new Set(deps.map((d) => dependencyPairKey(d.fromWorkUnitId, d.toWorkUnitId))),
    [deps],
  );
  const existingPriorityPairs = useMemo(
    () => new Set(priorityDeps.map((d) => dependencyPairKey(d.fromPriorityId, d.toPriorityId))),
    [priorityDeps],
  );

  const wuOptions = useMemo(
    () => workUnits.map((w) => ({ value: w.id, label: w.title })),
    [workUnits],
  );
  // Initiative endpoints are restricted to the active Input Scope where one is
  // set — offering the whole workspace here would let you build a dependency
  // the current scenario can never evaluate.
  const priorityOptions = useMemo(() => {
    const inScope = scopedItemIds
      ? priorities.filter((p) => scopedItemIds.has(p.id))
      : priorities;
    return inScope.map((p) => ({ value: p.id, label: p.name }));
  }, [priorities, scopedItemIds]);

  const isWuSatisfied = useCallback((dep: Dependency): boolean | null => {
    const rec = planRecordByWuId.get(dep.fromWorkUnitId);
    if (!rec) return null;
    return rec.dependencyStatus === 'Satisfied';
  }, [planRecordByWuId]);

  const isPrioritySatisfied = useCallback((dep: PriorityDependency): boolean | null => {
    const rec = priorityRecordById.get(dep.fromPriorityId);
    if (!rec) return null;
    return rec.dependencyStatus === 'Satisfied';
  }, [priorityRecordById]);

  // Show only deps where both endpoint work-units are in scope.
  const visibleWuDeps = useMemo(() => {
    if (!scopedItemIds) return deps;
    const inScope = (wuId: string) => {
      const wu = wuById.get(wuId);
      if (!wu) return false;
      if (wu.sourceId != null && scopedItemIds.has(wu.sourceId)) return true;
      if (wu.priorityId != null && scopedItemIds.has(wu.priorityId)) return true;
      return false;
    };
    return deps.filter((d) => inScope(d.fromWorkUnitId) && inScope(d.toWorkUnitId));
  }, [deps, wuById, scopedItemIds]);

  const visiblePriorityDeps = useMemo(() => {
    if (!scopedItemIds) return priorityDeps;
    return priorityDeps.filter(
      (d) => scopedItemIds.has(d.fromPriorityId) || scopedItemIds.has(d.toPriorityId),
    );
  }, [priorityDeps, scopedItemIds]);

  const sortOptions: SortOption<'dependencyType'>[] = [
    { field: 'dependencyType', label: 'Type', defaultDirection: 'asc' },
  ];

  const wuColumns: TableColumnWithRender<Dependency>[] = useMemo(
    () => [
      {
        key: 'from',
        label: 'Dependent',
        minWidth: 200,
        flex: true,
        render: (d) => {
          const title = wuById.get(d.fromWorkUnitId)?.title ?? d.fromWorkUnitId;
          return (
            <span className="block truncate min-w-0 w-full font-medium" title={title}>
              {title}
            </span>
          );
        },
      },
      {
        key: 'to',
        label: 'Predecessor',
        minWidth: 200,
        flex: true,
        render: (d) => {
          const title = wuById.get(d.toWorkUnitId)?.title ?? d.toWorkUnitId;
          return (
            <span className="block truncate min-w-0 w-full font-medium" title={title}>
              {title}
            </span>
          );
        },
      },
      { key: 'type', label: 'Type', sortable: true, minWidth: 72, render: (d) => d.dependencyType },
      { key: 'blocking', label: 'Blocking', minWidth: 72, render: (d) => (d.blocking ? 'Yes' : '—') },
      {
        key: 'satisfied',
        label: 'Satisfied',
        minWidth: 80,
        render: (d) => {
          const s = isWuSatisfied(d);
          return s === null ? '—' : s ? '✓' : '✗';
        },
      },
    ],
    [wuById, isWuSatisfied],
  );

  const initiativeColumns: TableColumnWithRender<PriorityDependency>[] = useMemo(
    () => [
      {
        key: 'from',
        label: 'Dependent initiative',
        minWidth: 200,
        flex: true,
        render: (d) => {
          const title = priorityById.get(d.fromPriorityId)?.name ?? d.fromPriorityId;
          return (
            <span className="block truncate min-w-0 w-full font-medium" title={title}>
              {title}
            </span>
          );
        },
      },
      {
        key: 'to',
        label: 'Predecessor initiative',
        minWidth: 200,
        flex: true,
        render: (d) => {
          const title = priorityById.get(d.toPriorityId)?.name ?? d.toPriorityId;
          return (
            <span className="block truncate min-w-0 w-full font-medium" title={title}>
              {title}
            </span>
          );
        },
      },
      { key: 'type', label: 'Type', sortable: true, minWidth: 72, render: (d) => d.dependencyType },
      { key: 'blocking', label: 'Blocking', minWidth: 72, render: (d) => (d.blocking ? 'Yes' : '—') },
      {
        key: 'satisfied',
        label: 'Satisfied',
        minWidth: 80,
        render: (d) => {
          const s = isPrioritySatisfied(d);
          return s === null ? '—' : s ? '✓' : '✗';
        },
      },
      {
        key: 'decision',
        label: 'Viability decision',
        minWidth: 120,
        render: (d) => priorityRecordById.get(d.fromPriorityId)?.planDecision ?? '—',
      },
    ],
    [priorityById, isPrioritySatisfied, priorityRecordById],
  );

  return (
    <>
      <div className="px-4 pt-3 flex flex-wrap items-center gap-2 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
        <div className="inline-flex border border-[var(--rule)] dark:border-[var(--rule)] p-0.5 text-xs font-medium">
          <button
            type="button"
            onClick={() => { setLevel('initiative'); setSelectedId(null); }}
            className={`px-3 py-1.5  transition-colors ${
              level === 'initiative'
                ? 'bg-primary-600 text-white'
                : 'text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            Initiatives ({visiblePriorityDeps.length})
          </button>
          <button
            type="button"
            onClick={() => { setLevel('work_unit'); setSelectedId(null); }}
            className={`px-3 py-1.5  transition-colors ${
              level === 'work_unit'
                ? 'bg-primary-600 text-white'
                : 'text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            Work units ({visibleWuDeps.length})
          </button>
        </div>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          {level === 'initiative'
            ? 'Hard initiative → initiative blocks drive Blocked on Above/Below.'
            : 'Work-unit grain dependencies (optional decomposition).'}
        </p>
        {syncing && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--text-ter)]" />}
      </div>

      {level === 'initiative' ? (
        <>
        <PlanningEntityListPage
          items={visiblePriorityDeps}
          loading={loading}
          getId={(d) => d.id}
          title="Initiative dependencies"
          subtitle="Hard blocking predecessors between initiatives (priorities)"
          icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
          sortOptions={sortOptions}
          defaultSort={{ field: 'dependencyType', direction: 'asc' }}
          facets={DEP_FACETS}
          tableColumns={initiativeColumns}
          renderCard={(d, { selected }) => {
            const from = priorityById.get(d.fromPriorityId)?.name ?? d.fromPriorityId;
            const to = priorityById.get(d.toPriorityId)?.name ?? d.toPriorityId;
            const decision = priorityRecordById.get(d.fromPriorityId)?.planDecision;
            return (
              <div className="p-4">
                <p className="text-sm font-medium truncate">{from}</p>
                <p className="text-xs text-[var(--text-ter)] mt-1">requires {to}</p>
                {decision && (
                  <p className="text-xs mt-1 text-[var(--mustard)] dark:text-[var(--mustard)]">{decision}</p>
                )}
                {selected && <p className="text-xs text-[var(--text-ter)] mt-2">Selected</p>}
              </div>
            );
          }}
          renderDetail={(d) => {
            const from = priorityById.get(d.fromPriorityId);
            const to = priorityById.get(d.toPriorityId);
            const satisfied = isPrioritySatisfied(d);
            const decision = priorityRecordById.get(d.fromPriorityId);
            const isSynced = d.notes?.startsWith(PLANNING_SYNC_DEP_NOTE_PREFIX);
            return (
              <div className="p-6 space-y-4 h-full overflow-auto">
                <div className="flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-[var(--text-ter)]" />
                  <h3 className="text-lg font-semibold">Initiative dependency</h3>
                </div>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-[var(--text-ter)]">Dependent</dt>
                    <dd className="font-medium">{from?.name ?? d.fromPriorityId}</dd>
                  </div>
                  <div>
                    <dt className="text-[var(--text-ter)]">Predecessor (must be Included)</dt>
                    <dd>{to?.name ?? d.toPriorityId}</dd>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <dt className="text-[var(--text-ter)]">Type</dt>
                      <dd>{d.dependencyType}</dd>
                    </div>
                    <div>
                      <dt className="text-[var(--text-ter)]">Blocking</dt>
                      <dd>{d.blocking ? 'Yes' : 'No'}</dd>
                    </div>
                  </div>
                  <div>
                    <dt className="text-[var(--text-ter)]">Satisfied (last viability run)</dt>
                    <dd>{satisfied === null ? '— (run viability)' : satisfied ? 'Yes' : 'No'}</dd>
                  </div>
                  {decision && (
                    <div>
                      <dt className="text-[var(--text-ter)]">Dependent decision</dt>
                      <dd className="font-medium">{decision.planDecision}</dd>
                      <dd className="text-xs text-[var(--text-ter)] mt-0.5">{decision.decisionReason}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-[var(--text-ter)]">Source</dt>
                    <dd>
                      {isSynced
                        ? 'Relationship in Prioritization (synced)'
                        : 'Created here in Planning'}
                    </dd>
                  </div>
                  {/* The sync marker is an internal id, not a note — showing it raw
                      leaked `sync:item_relationship:<uuid>` into the panel. */}
                  {d.notes && !isSynced && (
                    <div>
                      <dt className="text-[var(--text-ter)]">Notes</dt>
                      <dd>{d.notes}</dd>
                    </div>
                  )}
                </dl>
                {isSynced ? (
                  <p className="text-xs text-[var(--text-ter)]">
                    This dependency is owned by the item relationship in Prioritization. To remove
                    it, dissolve that relationship in Prioritization — deleting it here would only
                    last until the next sync.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (!confirm('Delete this initiative dependency?')) return;
                      void deletePriorityDependency(d.id).then(() => {
                        setSelectedId(null);
                        void load();
                      });
                    }}
                    className="text-sm text-[var(--coral)] hover:underline"
                  >
                    Delete dependency
                  </button>
                )}
              </div>
            );
          }}
          selectedId={selectedId}
          onSelectionChange={setSelectedId}
          urlParamName="priorityDependency"
          onRefresh={() => void handleSync()}
          onExport={() =>
            exportCsv(
              `initiative-dependencies-${new Date().toISOString().slice(0, 10)}.csv`,
              ['id', 'from', 'to', 'type', 'blocking'],
              visiblePriorityDeps.map((d) => [
                d.id,
                d.fromPriorityId,
                d.toPriorityId,
                d.dependencyType,
                d.blocking ? 'true' : 'false',
              ]),
            )
          }
          onCreate={priorityOptions.length >= 2 ? () => setCreating(true) : undefined}
          createLabel="Add initiative dependency"
          emptyTitle="No initiative dependencies"
          emptyDescription="Hard initiative → initiative dependencies block viability until the predecessor is Included. Add one here, or create a hard dependency between items in Prioritization and it will sync in."
          error={error}
          activeScenario={activeScenario}
          itemLabel="dependency"
        />
        <CreateDependencyModal
          isOpen={creating}
          onClose={() => setCreating(false)}
          options={priorityOptions}
          entityLabel="initiative"
          existingPairs={existingPriorityPairs}
          onSave={handleCreatePriority}
        />
        </>
      ) : (
        <>
          <PlanningEntityListPage
            items={visibleWuDeps}
            loading={loading}
            getId={(d) => d.id}
            title="Work-unit dependencies"
            subtitle={
              scopedItemIds
                ? `Scoped — showing ${visibleWuDeps.length} of ${deps.length} dependencies`
                : 'Hard blocking dependencies between work units'
            }
            icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
            sortOptions={sortOptions}
            defaultSort={{ field: 'dependencyType', direction: 'asc' }}
            facets={DEP_FACETS}
            tableColumns={wuColumns}
            renderCard={(d, { selected }) => {
              const from = wuById.get(d.fromWorkUnitId)?.title ?? d.fromWorkUnitId;
              const to = wuById.get(d.toWorkUnitId)?.title ?? d.toWorkUnitId;
              return (
                <div className="p-4">
                  <p className="text-sm font-medium truncate">{from}</p>
                  <p className="text-xs text-[var(--text-ter)] mt-1">requires {to}</p>
                  {selected && <p className="text-xs text-[var(--text-ter)] mt-2">Selected</p>}
                </div>
              );
            }}
            renderDetail={(d) => (
              <DependencyDetailPanel
                dep={d}
                fromWu={wuById.get(d.fromWorkUnitId)}
                toWu={wuById.get(d.toWorkUnitId)}
                satisfied={isWuSatisfied(d)}
                workUnits={workUnits}
                onUpdated={() => void load()}
                onDeleted={() => {
                  setSelectedId(null);
                  void load();
                }}
              />
            )}
            selectedId={selectedId}
            onSelectionChange={setSelectedId}
            urlParamName="dependency"
            onRefresh={() => void handleSync()}
            onExport={() =>
              exportCsv(
                `planning-dependencies-${new Date().toISOString().slice(0, 10)}.csv`,
                ['id', 'from', 'to', 'type', 'blocking'],
                deps.map((d) => [
                  d.id,
                  d.fromWorkUnitId,
                  d.toWorkUnitId,
                  d.dependencyType,
                  d.blocking ? 'true' : 'false',
                ]),
              )
            }
            onCreate={workUnits.length >= 2 ? () => setCreating(true) : undefined}
            createLabel="Add work-unit dependency"
            emptyTitle="No work-unit dependencies"
            emptyDescription="Dependencies sync automatically from item relationships in Prioritization. Add manual dependencies below."
            error={error}
            activeScenario={activeScenario}
            itemLabel="dependency"
          />
          <CreateDependencyModal
            isOpen={creating}
            onClose={() => setCreating(false)}
            options={wuOptions}
            entityLabel="work unit"
            existingPairs={existingWuPairs}
            onSave={handleCreateWu}
          />
        </>
      )}
    </>
  );
}
